import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconAvatarOutline } from '@icons/IconAvatarOutline'
import { Badge } from '@/components/shadcn/badge'

// Form validation schema
const formSchema = z.object({
  selectedUserIds: z.array(z.number()).min(1, 'Please select at least one team member to add'),
})

export type SupportTeamForm = z.infer<typeof formSchema>

type MessagesApplicationsApplicationTeam = RestSchemas['Messages.Applications.ApplicationTeam']
type MessagesSubscriptionsOrganisationTeamUser = RestSchemas['Messages.Subscriptions.OrganisationTeamUser']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

type SupportTeamFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedSupportTeam: MessagesApplicationsApplicationTeam
}

export const SupportTeamForm = (props: SupportTeamFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: SupportTeamFormProps) => {
  const {
    getSubscriptionsApiListOrganisationUsers,
    getApplicationsApiGetApplicationSupportTeamUsersForOrg,
    getSubscriptionsApiListTeamUsers,
    getApplicationsApiGetApplicationTeamsForUserOrganization,
    postSubscriptionsApiAddTeamUser,
    deleteSubscriptionsApiDeleteTeamUser,
  } = useRestClient()

  const {
    data: allOrgUsers,
    isPending: allOrgUsersLoading,
    isError: allOrgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery<MessagesSubscriptionsOrganisationUser[]>(
    { query: { orgId: props.selectedSupportTeam?.organisationId } },
    {
      enabled: !!props.selectedSupportTeam?.organisationId,
    }
  )

  const {
    data: currentTeamUsers,
    isPending: currentTeamUsersLoading,
    isError: currentTeamUsersError,
  } = getSubscriptionsApiListTeamUsers.useQuery<MessagesSubscriptionsOrganisationTeamUser[]>(
    { query: { teamId: props.selectedSupportTeam?.id } },
    {
      select: (data) =>
        data.filter(
          (user) =>
            user.userId &&
            user.userId !== 0 &&
            user.id &&
            user.id !== 0 &&
            user.teamId === props.selectedSupportTeam?.id
        ),
      enabled: !!props.selectedSupportTeam?.id,
    }
  )

  const { mutateAsync: addTeamUser } = postSubscriptionsApiAddTeamUser.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Team members successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding team members')
    },
  })

  const { mutate: deleteTeamUser } = deleteSubscriptionsApiDeleteTeamUser.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Team member successfully removed')
    },
    onError: () => {
      toast.error('Error removing team member')
    },
  })

  const invalidateQueries = () => {
    getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries({
      parameters: { path: { appId: props.selectedSupportTeam?.applicationId || 0 } },
    })
    getSubscriptionsApiListTeamUsers.invalidateQueries({
      parameters: { query: { teamId: props.selectedSupportTeam?.id } },
    })
    getApplicationsApiGetApplicationSupportTeamUsersForOrg.invalidateQueries({
      parameters: { path: { appId: props.selectedSupportTeam?.applicationId || 0 } },
    })
  }

  const defaultValues = useMemo(() => {
    return {
      selectedUserIds: [] as number[],
    }
  }, [])

  const form = useForm<SupportTeamForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Helper function to get user display name
  const getUserDisplayName = (user: MessagesSubscriptionsOrganisationUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : (user.email ?? 'Unknown User')
  }

  // Create user options with disambiguation for duplicate names
  const userOptions: SelectOption<number>[] = useMemo(() => {
    if (!allOrgUsers) return []

    // Filter out users with id 0 and users already in the team
    const availableUsers = allOrgUsers.filter((user) => {
      const isNotZeroId = user.id !== 0
      const isNotInCurrentTeam = !currentTeamUsers?.some(
        (teamUser) => teamUser.userId === user.id && teamUser.teamId === props.selectedSupportTeam?.id
      )
      return isNotZeroId && isNotInCurrentTeam
    })

    // Remove duplicates by user ID
    const seenUserIds = new Set<number>()
    const uniqueUsers = availableUsers.filter((user) => {
      if (seenUserIds.has(user.id)) {
        return false
      }
      seenUserIds.add(user.id)
      return true
    })

    // Sort users by family name
    const sortedUsers = [...uniqueUsers].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    // Count name occurrences for disambiguation
    const nameMap: Record<string, number> = {}
    sortedUsers.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameMap[displayName] = (nameMap[displayName] || 0) + 1
    })

    // Create options with disambiguation when needed
    return sortedUsers.map((user) => {
      const baseName = getUserDisplayName(user)
      const label = nameMap[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName

      return {
        value: user.id,
        label,
      }
    })
  }, [allOrgUsers, currentTeamUsers, props.selectedSupportTeam?.id])

  const handleSubmit = async (data: SupportTeamForm) => {
    const promises = data.selectedUserIds.map((userId) =>
      addTeamUser({
        body: {
          teamId: props.selectedSupportTeam!.id,
          userId: userId,
        },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteTeamUser = (teamUser: MessagesSubscriptionsOrganisationTeamUser) => {
    deleteTeamUser({
      body: {
        ...teamUser,
        teamId: props.selectedSupportTeam?.id ?? 0,
      },
    })
  }

  if (allOrgUsersLoading || currentTeamUsersLoading) return <FormComponentLoading />
  if (allOrgUsersError || currentTeamUsersError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedUserIds"
                label="Add Team Members"
                options={userOptions}
                placeholder="Select team members to add..."
                searchPlaceholder="Search users..."
                emptyMessage="No available users found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={userOptions.length === 0}>
                Add Selected Members
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {currentTeamUsers && currentTeamUsers.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Team Members:</div>
          <div className="space-y-2">
            {currentTeamUsers.map((teamUser) => (
              <TeamMemberCard
                key={`${teamUser.id}-${teamUser.teamId}-${teamUser.userId}`}
                teamUser={teamUser}
                onDelete={() => handleDeleteTeamUser(teamUser)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Team member card component
const TeamMemberCard = ({
  teamUser,
  onDelete,
}: {
  teamUser: MessagesSubscriptionsOrganisationTeamUser
  onDelete: () => void
}) => {
  const displayName =
    teamUser.givenName || teamUser.familyName
      ? `${teamUser.givenName ?? ''} ${teamUser.familyName ?? ''}`.trim()
      : (teamUser.email ?? 'Unknown User')

  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <IconAvatarOutline className="size-7 text-muted-foreground" />
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{displayName}</span>
          <Badge variant="outline">{teamUser.userId}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
